import React from "react";

/**
 * Reusable loading spinner with two visual styles:
 * - `border`: lightweight CSS spinner using Tailwind `animate-spin`
 * - `oval`: SVG ring spinner with configurable stroke colors/widths
 *
 * Use this component anywhere inline loading feedback is needed (buttons, cards,
 * section placeholders), while keeping a consistent motion style across the app.
 *
 * @param {Object} props
 * @param {string} [props.className=""] Extra Tailwind/CSS classes applied to the root element.
 * @param {string} [props.size="h-5 w-5"] Size class token(s) for spinner dimensions.
 * @param {"border"|"oval"} [props.variant="border"] Spinner style variant.
 * @param {string} [props.color="currentColor"] Primary spinner color (border color or oval arc).
 * @param {string|null} [props.secondaryColor=null] Secondary ring color for `oval`; falls back to `color`.
 * @param {number} [props.strokeWidth=4] Primary stroke width for `oval`.
 * @param {number|null} [props.strokeWidthSecondary=null] Secondary stroke width for `oval`; falls back to `strokeWidth`.
 * @param {number} [props.spinDurationSeconds=1] Full rotation duration in seconds.
 *   `1` matches Tailwind `animate-spin`; larger values spin more slowly.
 * @returns {JSX.Element}
 */
const Spinner = ({
  className = "",
  size = "h-5 w-5",
  variant = "border", // "border" or "oval"
  color = "currentColor",
  secondaryColor = null, // For oval variant
  strokeWidth = 4,
  strokeWidthSecondary = null,
  spinDurationSeconds = 1,
}) => {
  if (variant === "oval") {
    // Oval-style spinner to match the old LoadingSpinner exactly
    const finalSecondaryColor = secondaryColor || color;
    const finalStrokeWidthSecondary = strokeWidthSecondary || strokeWidth;
    
    return (
      <div
        className={`inline-block ${size} ${className}`}
        role="status"
        aria-label="oval-loading"
      >
        <svg
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
          style={{ width: "100%", height: "100%" }}
        >
          {/* Background circle (full oval) */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={finalSecondaryColor}
            strokeWidth={finalStrokeWidthSecondary}
            strokeLinecap="round"
            opacity="0.3"
          />
          {/* Animated arc (partial oval) */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray="70 200"
            strokeDashoffset="0"
            transform="rotate(-90 50 50)"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="0 50 50;360 50 50"
              dur={`${spinDurationSeconds}s`}
              repeatCount="indefinite"
            />
          </circle>
        </svg>
        <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
          Loading...
        </span>
      </div>
    );
  }

  // Default border-style spinner
  return (
    <div
      className={`inline-block animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite] ${size} ${className}`}
      style={{ animationDuration: `${spinDurationSeconds}s` }}
      role="status"
      aria-label="Loading"
    >
      <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
        Loading...
      </span>
    </div>
  );
};

export default Spinner;